import { useTranslation } from "react-i18next"
import PageTitle from "../../components/PageTitle"
import styled from "styled-components"
import { useEffect, useState } from "react"
import Comm from "../../Comm"
import WhiteSpace from "../../components/Whitespace"
import ChargerType from "../../types/ChargerType"
import Modal from "../../components/Modal"
import Button from "../../components/Button"
import InputWithLabel from "../../components/InputWithLabel"
import dateToTimeAgoString from "../../utils/dateToTimeAgoString"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import { useOutletContext } from "react-router-dom"
import { child, get, getDatabase, ref } from "firebase/database"
import { getAuth } from "firebase/auth"
import fbConf from "../../FirebaseConf"
import ExportButton from "../../modules/ExportButton"
import LoaderDots from "../../components/LoaderDots"
import ChargerDetailsModal, { ChargerDetailsHint } from "../../modules/ChargerDetailsModal"
import { ChargerFilterModal, chargerMatchesFilter, countActiveChargerFilters, ChargerFilter } from "../../modules/ChargerFilter"
import OveruseModal from "../../modules/OveruseModal"
import { useConfirm } from "../../ConfirmProvider"
import { useNotification } from "../../modules/Notification"
import { BREAKPOINTS } from "../../GlobalTheme"

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

const ToolbarContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 12px;
  display: flex;
  /* Absolutely positioned toolbars cannot wrap, so on a phone the button row overlays the page
     title and its leading buttons land at negative x, which no amount of panning can reach.
     Put it back in flow and let it wrap. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    right: auto;
    left: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
`

const SmallModalContainer = styled.div`
  padding: 24px;
`

const SmallModalButtonContainer = styled.div`
  width: min(max(15vw, 192px), calc(100vw - 72px));
  display: flex;
`


// Per-charge report is an AlphaVet-only feature; gate the button by their account UID (VLT-2149)
const ALPHAVET_UID = 'Bmkwb1NtraOxbZmS7Dul4fQwZs42'

export default function Chargers() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { addNotification } = useNotification()
  const { isAdmin } = useOutletContext<{ isAdmin: boolean }>()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false)

  // Per-charge charging report modal (AlphaVet-only) (VLT-2149)
  const [isShowingReportModal, setIsShowingReportModal] = useState<boolean>(false)
  const [reportFromInput, setReportFromInput] = useState<string>('')
  const [reportToInput, setReportToInput] = useState<string>('')
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false)

  const [chargers, setChargers] = useState<ChargerType[]>()
  const [detailsCharger, setDetailsCharger] = useState<ChargerType | null>(null)
  const [chargerFilter, setChargerFilter] = useState<ChargerFilter>({})
  const [isShowingFilterModal, setIsShowingFilterModal] = useState<boolean>(false)
  const [isShowingOveruseModal, setIsShowingOveruseModal] = useState<boolean>(false)
  const [lastRefresh, setLastRefresh] = useState<string>('')
  const [isEditingChargerValues, setIsEditingChargerValues] = useState<ChargerType | null>()
  const [newSerialNumberInputValue, setNewSerialNumberInputValue] = useState<string>('')
  const [newChargerIdInputValue, setNewChargerIdInputValue] = useState<string>('')
  const [newLocalNameInputValue, setNewLocalNameInputValue] = useState<string>('')

  const [triggerTableSearchAgain, setTriggerTableSearchAgain] = useState<number>(0)

  const [rfidStatuses, setRfidStatuses] = useState<{ [key: string]: "Active" | "Inactive" | "-" } | null>(null)


  async function fetchChargersForAdmin() {
    const response = await Comm('/api-charger/chargers', {})
    if (response.status != 200) {
      if (response.status == 204) {
        // No chargers: show an empty table instead of falling through to response.json() (crashes on empty body) (VLT-2169)
        setChargers([])
        setIsLoading(false)
        return
      } else {
        throw new Error('Failed to fetch chargers for admin, response is not 200 or 204')
      }
    }
    const result = await response.json()
    setLastRefresh(new Date().toString().split(' ')[4])

    // Iterare over all chargers and set the online field based on the online endpoint
    const onlineChargersResponse = await Comm('/api-websocket/online', {})
    if (onlineChargersResponse.status != 200) { throw new Error('Failed to fetch online chargers for admin, response is not 200') }
    const onlineChargers = await onlineChargersResponse.json()
    for (const charger of result) {
      charger.online = onlineChargers.includes(charger.chargerId)
    }

    setIsLoading(false)
    setChargers(result)
  }

  async function fetchChargersForUser() {
    const response = await Comm('/api-charger/user/chargers', {})
    if (response.status != 200) {
      if (response.status == 204) {
        // No chargers: show an empty table instead of falling through to response.json() (crashes on empty body) (VLT-2169)
        setChargers([])
        setIsLoading(false)
        return
      } else {
        throw new Error('Failed to fetch chargers for user, response is not 200 or 204')
      }
    }
    const result = await response.json()
    setLastRefresh(new Date().toString().split(' ')[4])

    if (result.length > 0) {
      // Get users chargers from firebase to get the firebase charger name
      const firebaseChargerNamesResponse = await get(child(ref(getDatabase(fbConf)), `voltie/chargerusers/${result[0].userId}/chargers`))
      const firebaseChargerNames = await firebaseChargerNamesResponse.val()

      // Iterare over all chargers and set the online field based on the online endpoint
      const onlineChargersResponse = await Comm('/api-websocket/online', {})
      if (onlineChargersResponse.status != 200) { throw new Error('Failed to fetch online for user, response is not 200') }
      const onlineChargers = await onlineChargersResponse.json()
      for (const charger of result) {
        charger.online = onlineChargers.includes(charger.chargerId)
        charger.localName = firebaseChargerNames[charger.chargerId]?.name || charger.localName
      }
    }

    setIsLoading(false)
    setChargers(result)
  }

  useEffect(() => {
    handelRefresh()
  }, [])

  async function handelRefresh() {
    setIsLoading(true)

    try {
      if (isAdmin) {
        await fetchChargersForAdmin()
      } else {
        await fetchChargersForUser()
      }
    } catch (err) {
      addNotification(`Error while loading chargers: ${err}`, "TEXT", "error")
      setChargers([])
    } finally {
      setIsLoading(false)
    }

    setTriggerTableSearchAgain((prev) => prev + 1)
  }

  async function handleEditChargerValues() {
    if (isEditingChargerValues) {
      await Comm('/api-charger/charger/update', {
        method: 'PUT',
        body: JSON.stringify({
          chargerId: isEditingChargerValues.chargerId,
          serialNumber: newSerialNumberInputValue.length > 0 ? newSerialNumberInputValue : ' ',
          firmwareVersion: '',
          softwareVersion: '',
          osVersion: '',
          localName: newLocalNameInputValue.length > 0 ? newLocalNameInputValue : ' ',
          initedCharger: {
            chargerId: '',
            userId: ''
          }
        }),
        headers: {
          'charger_id': isEditingChargerValues.chargerId,
          'Content-Type': 'application/json'
        }
      })
      isEditingChargerValues.serialNumber = newSerialNumberInputValue
      isEditingChargerValues.chargerId = newChargerIdInputValue
      isEditingChargerValues.localName = newLocalNameInputValue
      setNewSerialNumberInputValue('')
      setNewChargerIdInputValue('')
      setNewLocalNameInputValue('')
      setIsEditingChargerValues(null)

      setTriggerTableSearchAgain((prev) => prev + 1)
    }
  }

  async function getRfidStatuses() {
    if (chargers) {

      const confirmed = await confirm({ message: t('chargers_thisIsGoingToSendACommandToAllYourChargersAreYouSure'), showCancel: true })
      if (!confirmed) return

      setRfidStatuses({})

      for (const charger of chargers) {
        const response = await Comm('/api-websocket/mobile', {
          method: "POST",
          headers: {
            "Charger_id": charger.chargerId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ "msg_type": 23 })
        })

        if (response.ok) {
          const result = await response.json()
          //(result.rfid_stat.rfid_available && (result.rfid_stat.access_mode === 1 || result.rfid_stat.access_mode === 0))
          setRfidStatuses(prev => ({ ...(prev || {}), [charger.chargerId]: (result.rfid_stat.rfid_available && result.rfid_stat.access_mode === 1) ? 'Active' : 'Inactive' }))
        } else {
          setRfidStatuses(prev => ({ ...(prev || {}), [charger.chargerId]: '-' }))
        }
        setTriggerTableSearchAgain((prev) => prev + 1)
      }
    }
  }

  // Set the values of the 3 input fields when editing a chargers values begins
  useEffect(() => {
    if (isEditingChargerValues) {
      setNewSerialNumberInputValue(isEditingChargerValues.serialNumber || '')
      setNewChargerIdInputValue(isEditingChargerValues.chargerId || '')
      setNewLocalNameInputValue(isEditingChargerValues.localName || '')
    }
  }, [isEditingChargerValues])

/*const tempChargerRenaming = [{
    "chargerid": "00000000d9dc288a",
    "name": "Angol32_037"
  },
  {
    "chargerid": "000000003b607a64",
    "name": "Angol32_119"
  },
  {
    "chargerid": "00000000fc94b05d",
    "name": "Angol32_120"
  },
  {
    "chargerid": "0000000070f8ad50",
    "name": "Angol32_123"
  },
  {
    "chargerid": "00000000fa1d2b9e",
    "name": "Angol32_124"
  },
  {
    "chargerid": "0000000015419129",
    "name": "Angol32_125"
  },
  {
    "chargerid": "00000000a6ee701a",
    "name": "Angol32_126"
  },
  {
    "chargerid": "000000006fc80463",
    "name": "Angol32_127"
  },
  {
    "chargerid": "00000000f1d927d9",
    "name": "Angol32_128"
  },
  {
    "chargerid": "000000001c93b475",
    "name": "Angol32_129"
  },
  {
    "chargerid": "0000000068a40ed0",
    "name": "Angol32_130"
  },
  {
    "chargerid": "00000000053b15a3",
    "name": "Angol32_131"
  },
  {
    "chargerid": "0000000079aae24f",
    "name": "Angol32_132"
  },
  {
    "chargerid": "00000000189b52af",
    "name": "Angol32_133"
  },
  {
    "chargerid": "00000000cd6a3c24",
    "name": "Angol32_134"
  },
  {
    "chargerid": "0000000096a11083",
    "name": "Angol32_135"
  },
  {
    "chargerid": "0000000094d22612",
    "name": "Angol32_136"
  },
  {
    "chargerid": "000000008bc86b70",
    "name": "Angol32_137"
  },
  {
    "chargerid": "00000000e6174fe4",
    "name": "Angol32_138"
  },
  {
    "chargerid": "00000000866586b1",
    "name": "Angol32_139"
  },
  {
    "chargerid": "000000004f7ecc3c",
    "name": "Angol32_140"
  },
  {
    "chargerid": "00000000ce9bb839",
    "name": "Angol32_141"
  },
  {
    "chargerid": "000000002659c035",
    "name": "Angol32_142"
  },
  {
    "chargerid": "000000000ab5a2d1",
    "name": "Angol32_143"
  },
  {
    "chargerid": "00000000eeb7cf90",
    "name": "Angol32_145"
  },
  {
    "chargerid": "00000000f1dfc619",
    "name": "Angol32_151"
  },
  {
    "chargerid": "000000000e8f39f9",
    "name": "Angol32_152"
  },
  {
    "chargerid": "000000007e4ab2fa",
    "name": "Angol32_153"
  },
  {
    "chargerid": "0000000076f82809",
    "name": "Angol32_154"
  },
  {
    "chargerid": "000000000675942d",
    "name": "Angol32_155"
  },
  {
    "chargerid": "00000000accacb3f",
    "name": "Angol32_156"
  },
  {
    "chargerid": "000000009f20793f",
    "name": "Angol32_157"
  },
  {
    "chargerid": "00000000b801e149",
    "name": "Angol32_158"
  },
  {
    "chargerid": "000000003643f61c",
    "name": "Angol32_159"
  },
  {
    "chargerid": "000000003a7bb0db",
    "name": "Angol32_160"
  },
  {
    "chargerid": "000000005b18bb3d",
    "name": "NewPalace2-P1-145"
  },
  {
    "chargerid": "000000002b7ec1f0",
    "name": "NewPalace2-P1-146"
  },
  {
    "chargerid": "00000000f7e74f54",
    "name": "NewPalace2-P1-147"
  },
  {
    "chargerid": "000000009e572873",
    "name": "NewPalace2-P1-148"
  },
  {
    "chargerid": "000000009ec0ec0a",
    "name": "NewPalace2-P1-149"
  },
  {
    "chargerid": "00000000ce8023ca",
    "name": "NewPalace2-P1-150"
  },
  {
    "chargerid": "0000000086eb6fc0",
    "name": "NewPalace2-P1-151"
  },
  {
    "chargerid": "000000001bebcecf",
    "name": "NewPalace2-P1-152"
  },
  {
    "chargerid": "000000007bbd67a4",
    "name": "NewPalace2-P1-153"
  },
  {
    "chargerid": "00000000d7f48856",
    "name": "NewPalace2-P1-154"
  },
  {
    "chargerid": "00000000e0296f1f",
    "name": "NewPalace2-P1-155"
  },
  {
    "chargerid": "0000000025bd9d8c",
    "name": "NewPalace2-P1-156"
  },
  {
    "chargerid": "000000002a803b70",
    "name": "NewPalace2-P1-157"
  },
  {
    "chargerid": "000000006bc89323",
    "name": "NewPalace2-P1-158"
  },
  {
    "chargerid": "00000000f02d7e5a",
    "name": "NewPalace2-P1-159"
  },
  {
    "chargerid": "000000002b0d6b1c",
    "name": "NewPalace2-P1-160"
  },
  {
    "chargerid": "00000000fd99bd73",
    "name": "NewPalace2-P1-169"
  },
  {
    "chargerid": "000000003015e4e9",
    "name": "NewPalace2-P1-170"
  },
  {
    "chargerid": "0000000020593216",
    "name": "NewPalace2-P1-171"
  },
  {
    "chargerid": "00000000758c9a90",
    "name": "NewPalace2-P1-175"
  },
  {
    "chargerid": "000000005d125a0e",
    "name": "NewPalace2-P1-176"
  },
  {
    "chargerid": "00000000e0c4eed2",
    "name": "NewPalace2-P1-177"
  },
  {
    "chargerid": "00000000abf640b8",
    "name": "NewPalace2-P1-178"
  },
  {
    "chargerid": "000000007f80133c",
    "name": "NewPalace2-P1-179"
  },
  {
    "chargerid": "000000001d9bbd31",
    "name": "NewPalace2-P1-180"
  },
  {
    "chargerid": "0000000099cf4661",
    "name": "NewPalace2-P1-181"
  },
  {
    "chargerid": "0000000031f9110a",
    "name": "NewPalace2-P1-182"
  },
  {
    "chargerid": "0000000068492883",
    "name": "NewPalace2-P1-183"
  },
  {
    "chargerid": "0000000005913846",
    "name": "NewPalace2-P1-184"
  },
  {
    "chargerid": "00000000f64460be",
    "name": "NewPalace2-P1-185"
  },
  {
    "chargerid": "0000000039d9c719",
    "name": "NewPalace2-P1-186"
  },
  {
    "chargerid": "00000000f6a2ddb2",
    "name": "NewPalace2-P1-187"
  },
  {
    "chargerid": "000000006b4f93e2",
    "name": "NewPalace2-P1-188"
  },
  {
    "chargerid": "000000001c8130e0",
    "name": "NewPalace2-P1-189"
  },
  {
    "chargerid": "00000000f67ac0ae",
    "name": "NewPalace2-P1-190"
  },
  {
    "chargerid": "000000001264acf2",
    "name": "NewPalace2-P1-191"
  },
  {
    "chargerid": "000000006d74c33d",
    "name": "NewPalace2-P1-194"
  },
  {
    "chargerid": "0000000026136538",
    "name": "NewPalace2-P2-251"
  },
  {
    "chargerid": "000000000553d8f3",
    "name": "NewPalace2-P2-252"
  },
  {
    "chargerid": "000000001cd58c15",
    "name": "NewPalace2-P2-253"
  },
  {
    "chargerid": "000000003ba62e5b",
    "name": "NewPalace2-P2-254"
  },
  {
    "chargerid": "00000000e4ba478d",
    "name": "NewPalace2-P2-255"
  },
  {
    "chargerid": "00000000964b857a",
    "name": "NewPalace2-P2-256"
  },
  {
    "chargerid": "00000000f6bea456",
    "name": "NewPalace2-P2-257"
  },
  {
    "chargerid": "00000000880edb9d",
    "name": "NewPalace2-P2-258"
  },
  {
    "chargerid": "000000004d4716f7",
    "name": "NewPalace2-P2-259"
  },
  {
    "chargerid": "000000002b07f0bb",
    "name": "NewPalace2-P2-260"
  },
  {
    "chargerid": "00000000b78f077d",
    "name": "NewPalace2-P2-261"
  },
  {
    "chargerid": "00000000625d539a",
    "name": "NewPalace2-P2-262"
  },
  {
    "chargerid": "00000000a0603b8f",
    "name": "NewPalace2-P2-263"
  },
  {
    "chargerid": "000000004feb818d",
    "name": "NewPalace2-P2-264"
  },
  {
    "chargerid": "00000000d90c2a71",
    "name": "NewPalace2-P2-275"
  },
  {
    "chargerid": "000000002fb35d91",
    "name": "NewPalace2-P2-276"
  },
  {
    "chargerid": "0000000051de4541",
    "name": "NewPalace2-P2-277"
  },
  {
    "chargerid": "00000000327fe2b0",
    "name": "NewPalace2-P2-278"
  },
  {
    "chargerid": "000000000387443f",
    "name": "NewPalace2-P2-279"
  },
  {
    "chargerid": "000000009bda2794",
    "name": "NewPalace2-P2-280"
  },
  {
    "chargerid": "00000000949e77e4",
    "name": "NewPalace2-P2-281"
  },
  {
    "chargerid": "000000003d242a9c",
    "name": "NewPalace2-P2-282"
  },
  {
    "chargerid": "00000000480344ba",
    "name": "NewPalace2-P2-283"
  },
  {
    "chargerid": "00000000e844e2c2",
    "name": "NewPalace2-P2-284"
  },
  {
    "chargerid": "000000006194dfa0",
    "name": "NewPalace2-P2-285"
  },
  {
    "chargerid": "000000003df9a08c",
    "name": "NewPalace2-P2-286"
  },
  {
    "chargerid": "00000000f9cd2b2e",
    "name": "NewPalace2-P2-288"
  },
  {
    "chargerid": "0000000049de7688",
    "name": "NewPalace2-P2-289"
  },
  {
    "chargerid": "000000007f755c19",
    "name": "NewPalace2-P2-290"
  },
  {
    "chargerid": "00000000c3e2422e",
    "name": "NewPalace2-P2-291"
  },
  {
    "chargerid": "00000000fbbe0be2",
    "name": "NewPalace2-P2-292"
  },
  {
    "chargerid": "00000000fe26255d",
    "name": "NewPalace2-P2-293"
  }
]

  // TEMP, ONE TIME USE: renames chargers based on the tempChargerRenaming array
  async function setChargersLocalNameFromTempRenamingArray() {
    if (chargers) {
      for (const renaming of tempChargerRenaming) {
        const charger = chargers.find(c => c.chargerId === renaming.chargerid)
        if (!charger) {
          console.log(`Charger not found, skipping: ${renaming.chargerid} (${renaming.name})`)
          continue
        }
        console.log(`${charger.chargerId}: "${charger.localName}" -> "${renaming.name}"`)
        try {
          await Comm('/api-charger/charger/update', {
            method: 'PUT',
            body: JSON.stringify({
              chargerId: charger.chargerId,
              serialNumber: charger.serialNumber || "",
              firmwareVersion: '',
              softwareVersion: '',
              osVersion: '',
              localName: renaming.name,
              initedCharger: {
                chargerId: '',
                userId: ''
              }
            }),
            headers: {
              'charger_id': charger.chargerId,
              'Content-Type': 'application/json'
            }
          })
        } catch {
          console.log(`Something went wrong: ${renaming.chargerid} (${renaming.name})`)
        }
      }
      console.log('Renaming done')
    }
  }

  async function setChargersLocalNameToFirebaseName() {
    if (chargers) {
      for (const charger of chargers) {
        if (charger.userEmail === "info@freecharger.hu") {
          console.log(charger.chargerId)
          try {
            const firebaseNameResponse = await get(ref(getDatabase(fbConf), `voltie/chargerusers/${charger.userId}/chargers/${charger.chargerId}/name`))
            const firebaseName = firebaseNameResponse.val()
            await Comm('/api-charger/charger/update', {
              method: 'PUT',
              body: JSON.stringify({
                chargerId: charger.chargerId,
                serialNumber: charger.serialNumber || "",
                firmwareVersion: '',
                softwareVersion: '',
                osVersion: '',
                localName: firebaseName,
                initedCharger: {
                  chargerId: '',
                  userId: ''
                }
              }),
              headers: {
                'charger_id': charger.chargerId,
                'Content-Type': 'application/json'
              }
            })
          } catch {
            console.log("Something went wrong", "TEXT", "error")
          }
        }
      }
    }
  }*/

  // Admin charger + user report: backend generates the xlsx, we download the returned blob (VLT-2222)
  async function handleDownloadChargerReport() {
    setIsDownloadingReport(true)
    // The report can take a while to generate; let the admin know it started
    addNotification(t('charger_preparingTheDownload'), "TEXT")
    try {
      const response = await Comm('/api-charger/charger/report', {})
      if (response.ok) {
        const blob = new Blob([await response.arrayBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const downloadAnchorNode = document.createElement('a')
        downloadAnchorNode.setAttribute("href", url)
        downloadAnchorNode.setAttribute("download", "charger-user-report.xlsx")
        document.body.appendChild(downloadAnchorNode) // required for firefox
        downloadAnchorNode.click()
        downloadAnchorNode.remove()
        URL.revokeObjectURL(url)
      } else {
        addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
      }
    } catch {
      addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
    }
    setIsDownloadingReport(false)
  }

  function openAlphaVetReportModal() {
    // The button stays visible for admins (so we don't forget this feature), but the report is AlphaVet-only
    if (getAuth(fbConf).currentUser?.uid !== ALPHAVET_UID) {
      addNotification('This function is only available for the AlphaVet account', "TEXT")
      return
    }
    // Default to the current month-to-date
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    setReportFromInput(`${now.getFullYear()}-${month}-01`)
    setReportToInput(`${now.getFullYear()}-${month}-${String(now.getDate()).padStart(2, '0')}`)
    setIsShowingReportModal(true)
  }

  // AlphaVet-only per-charge charging report for the selected period (VLT-2149)
  async function downloadAlphaVetPerChargeReport() {
    if (!reportFromInput || !reportToInput || reportFromInput > reportToInput) {
      addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
      return
    }
    const userId = getAuth(fbConf).currentUser?.uid
    const from = Math.round(new Date(`${reportFromInput}T00:00:00`).getTime() / 1000)
    const to = Math.round(new Date(`${reportToInput}T23:59:59`).getTime() / 1000)

    setIsGeneratingReport(true)
    addNotification(t('charger_preparingTheDownload'), "TEXT")
    try {
      const response = await Comm(`/api-financial/report/excel/per-charge?from=${from}&to=${to}`, { headers: { "user_id": userId, "Content-Type": "application/json" } })
      if (response.ok) {
        const blob = new Blob([await response.arrayBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const downloadAnchorNode = document.createElement('a')
        downloadAnchorNode.setAttribute("href", url)
        downloadAnchorNode.setAttribute("download", `charging-report ${reportFromInput} - ${reportToInput}.xlsx`)
        document.body.appendChild(downloadAnchorNode) // required for firefox
        downloadAnchorNode.click()
        downloadAnchorNode.remove()
        URL.revokeObjectURL(url)
        setIsShowingReportModal(false)
      } else {
        addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
      }
    } catch {
      addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
    }
    setIsGeneratingReport(false)
  }

  // Chargers narrowed by the active device-field filter (VLT-2044 follow-up)
  const displayedChargers = chargers?.filter(charger => chargerMatchesFilter(charger, chargerFilter))

  // Rendered next to the table's search bar (like the ErrorLogs page). Admins only (VLT-2782): this
  // is the only way into the filter modal, so leaving it out removes the feature rather than hiding
  // a reachable control. Table renders nothing for a null slot.
  const filterButton = !isAdmin ? null : (
    <ButtonWithIcon icon="general.filter" label={countActiveChargerFilters(chargerFilter) > 0 ? `${t('chargers_filter')} (${countActiveChargerFilters(chargerFilter)})` : t('chargers_filter')} onClick={() => setIsShowingFilterModal(true)} backgroundColor={countActiveChargerFilters(chargerFilter) > 0 ? 'primary' : 'dark'} iconColor="white" />
  )

  return (
    <>
      {/*<button onClick={setChargersLocalNameToFirebaseName}>Run</button>
      <button onClick={setChargersLocalNameFromTempRenamingArray}>Run temp renaming</button>*/}
      <div style={{ display: 'flex' }}>
        <PageTitle label={t('chargers_chargers')} />
        {isLoading && <div style={{ marginTop: '19px', marginLeft: '4px' }}>
          <LoaderDots />
        </div>}
      </div>
      {isShowingReportModal && (
        <Modal onEscape={() => setIsShowingReportModal(false)}>
          <SmallModalContainer>
            <p style={{ fontWeight: 700, fontSize: '16px', margin: 0, marginBottom: '16px' }}>{t('chargers_alphaVetReport')}</p>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#5E6278' }}>{t('chargers_from')}</label>
            <WhiteSpace height="4px" />
            <input type="date" value={reportFromInput} max={reportToInput || undefined} onChange={e => setReportFromInput(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #E1E3EA', boxSizing: 'border-box' }} />
            <WhiteSpace height="12px" />
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#5E6278' }}>{t('chargers_to')}</label>
            <WhiteSpace height="4px" />
            <input type="date" value={reportToInput} min={reportFromInput || undefined} onChange={e => setReportToInput(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #E1E3EA', boxSizing: 'border-box' }} />
            <WhiteSpace height="20px" />
            <SmallModalButtonContainer>
              <Button onclick={() => setIsShowingReportModal(false)} label={t('chargers_cancel')} width="calc(50% - 8px)" />
              <WhiteSpace width="16px" />
              <Button onclick={isGeneratingReport ? () => { } : downloadAlphaVetPerChargeReport} label={isGeneratingReport ? '...' : t('chargers_downloadReport')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingOveruseModal && !isAdmin && (
        <OveruseModal chargers={chargers} onClose={() => setIsShowingOveruseModal(false)} />
      )}
      <ChargerDetailsModal charger={detailsCharger} onClose={() => setDetailsCharger(null)} />
      {isEditingChargerValues && (
        <Modal onEscape={() => setIsEditingChargerValues(null)}>
          <SmallModalContainer>
            <InputWithLabel autoFocus width='100%' label={t('chargers_newChargerId')} value={newChargerIdInputValue} onInput={() => { }} />
            <WhiteSpace height="16px" />
            <InputWithLabel width='100%' label={t('chargers_newSerialNumber')} value={newSerialNumberInputValue} onInput={(value) => setNewSerialNumberInputValue(value)} />
            <WhiteSpace height="16px" />
            <InputWithLabel width='100%' label={t('chargers_newLocalName')} value={newLocalNameInputValue} onInput={(value) => setNewLocalNameInputValue(value)} />
            <WhiteSpace height="16px" />
            <SmallModalButtonContainer>
              <Button onclick={handleEditChargerValues} label={t('chargers_save')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
              <WhiteSpace width="16px" />
              <Button onclick={() => setIsEditingChargerValues(null)} label={t('chargers_cancel')} width="calc(50% - 8px)" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingFilterModal && chargers && (
        <ChargerFilterModal
          chargers={chargers}
          filter={chargerFilter}
          onChange={(f) => { setChargerFilter(f); setTriggerTableSearchAgain((prev) => prev + 1) }}
          onClose={() => setIsShowingFilterModal(false)}
        />
      )}
      <ToolbarContainer>

        {/* Dont show to admins for now */ !isAdmin && <>
          <div>
            <ButtonWithIcon icon="general.downArrow" label={t('chargers_getRfidStatuses')} onClick={getRfidStatuses} backgroundColor="gray_900" iconColor="white" />
          </div>
          <WhiteSpace width="8px" />
        </>}
        {/* Users only: the config is keyed by the signed-in account's own chargers (VLT-2039). */}
        {!isAdmin && <>
          <div>
            <ButtonWithIcon icon="chargers.time" label={t('chargers_overuseMonitoring')} onClick={() => setIsShowingOveruseModal(true)} backgroundColor="dark" iconColor="white" />
          </div>
          <WhiteSpace width="8px" />
        </>}
        {(isAdmin || getAuth(fbConf).currentUser?.uid === ALPHAVET_UID) && <>
          <div>
            <ButtonWithIcon icon="general.download" label={t('chargers_alphaVetReport')} onClick={openAlphaVetReportModal} backgroundColor="dark" iconColor="white" />
          </div>
          <WhiteSpace width="8px" />
        </>}
        {isAdmin && <>
          <div>
            <ButtonWithIcon icon="general.download" label={isDownloadingReport ? '...' : t('chargers_downloadReport')} onClick={isDownloadingReport ? () => { } : handleDownloadChargerReport} backgroundColor="dark" iconColor="white" />
          </div>
          <WhiteSpace width="8px" />
        </>}
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('chargers_refresh')} onClick={handelRefresh} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width="8px" />
        <div>
          {displayedChargers && <ExportButton fileName="Chargers" titleRow={[
            { label: 'Charger ID', width: '20' },
            { label: 'Serial Number', width: '15' },
            { label: 'Charger Name', width: '30' },
            { label: 'Online', width: '10' },
            { label: 'Last Check-in', width: '20' },
            { label: 'Owner Email', width: '40' },
            { label: 'FW Version', width: '15' },
            { label: 'SW Version', width: '15' },
            { label: 'OS Version', width: '15' },
          ]} data={displayedChargers.map(charger => [charger.chargerId, charger.serialNumber || ' ', charger.localName, charger.online ? 'Online' : 'Offline', charger.lastPresence, charger.userEmail, charger.fwVersion, charger.swVersion, charger.osVersion])} />
          }
        </div>
      </ToolbarContainer>
      {isAdmin ? <>
        {/* Chargers table for admins */}
        {displayedChargers && displayedChargers.length > 0 ? (
          <Table id="Chargers" searchBarSlot={filterButton} height="calc(90vh - 170px)" isSearchEnabled={true} isPaginationEnabled={true} triggerRefresh={triggerTableSearchAgain} columns={[
            { width: '30px' },
            { label: t('chargers_chargerId'), width: rfidStatuses ? '11%' : '13%' },
            { label: t('chargers_userIdUserEmail'), width: '20%' },
            { label: t('chargers_status'), width: rfidStatuses ? '6%' : '8%' },
            ...(rfidStatuses ? [{ label: t('chargers_rfidStatus'), width: '6%' }] : []),
            { label: t('chargers_lastOnline'), width: '9%' },
            { label: t('chargers_registrationDate'), width: '9%' },
            { label: t('chargers_fwVersion'), width: '6%' },
            { label: t('chargers_swVersion'), width: '6%' },
            { label: t('chargers_osVersion'), width: '6%' },
            { label: t('chargers_serialNumberLocalName') },
            { width: '12px' },
            { width: '24px' },
            { width: '70px' }
          ]} data={displayedChargers.map(charger => [
            {
              link: `/chargers/${charger.chargerId}`,
              value: {
                type: TableCellTypes.Image,
                src: charger.online ? 'chargers.chargerOnline' : 'chargers.chargerOffline'
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.chargerId,
              value: {
                type: TableCellTypes.Label,
                value: charger.chargerId
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.userId,
              value: {
                type: TableCellTypes.LabelDivided,
                value1: charger.userId,
                value2: charger.userEmail
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.online ? -Infinity : new Date(charger.lastPresence.replace(/\./g, '-')).valueOf(),
              value: {
                type: TableCellTypes.Status,
                label: charger.online ? 'Online' : 'Offline',
                themeColor: charger.online ? 'success' : 'gray_400',
                labelBelow: charger.online ? null : dateToTimeAgoString(new Date(charger.lastPresence.replace(/\./g, '-')))
              }
            },
            ...(rfidStatuses ? [{
              link: `/chargers/${charger.chargerId}`,
              sortBy: rfidStatuses[charger.chargerId] || '',
              value: {
                type: TableCellTypes.Status as TableCellTypes.Status,
                label: rfidStatuses[charger.chargerId] ? rfidStatuses[charger.chargerId] : <LoaderDots />,
                themeColor: rfidStatuses[charger.chargerId] ? (rfidStatuses[charger.chargerId] === 'Inactive' || rfidStatuses[charger.chargerId] === '-') ? 'gray_400' : 'success' : 'gray_400'
              }
            }] : []),
            {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.lastPresence,
              value: {
                type: TableCellTypes.LabelDivided,
                value1: charger.lastPresence != '1900.01.01 01:01' ? charger.lastPresence.split(' ')[0] : '-',
                value2: charger.lastPresence != '1900.01.01 01:01' ? charger.lastPresence.split(' ')[1] : ''
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.registrationDate,
              value: {
                type: TableCellTypes.LabelDivided,
                value1: (charger.registrationDate || '').split(' ')[0],
                value2: (charger.registrationDate || '').split(' ')[1]
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: (parseInt(charger.fwVersion?.split('.')[0]) || 0) * 100 + (parseInt(charger.fwVersion?.split('.')[1]) || 0),
              value: {
                type: TableCellTypes.Label,
                value: charger.fwVersion || '-'
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: (parseInt(charger.swVersion?.split('.')[0]) || 0) * 10000 + (parseInt(charger.swVersion?.split('.')[1]) || 0) * 100 + (parseInt(charger.swVersion?.split('.')[2]) || 0),
              value: {
                type: TableCellTypes.Label,
                value: charger.swVersion || '-'
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: parseInt(charger.osVersion) || 0,
              value: {
                type: TableCellTypes.Label,
                value: charger.osVersion || '-'
              }
            }, {
              onClick: () => setIsEditingChargerValues(charger),
              sortBy: charger.serialNumber == ' ' ? '-' : charger.serialNumber || '-',
              value: {
                type: TableCellTypes.LabelDivided,
                value1: charger.serialNumber == ' ' ? '-' : charger.serialNumber || '-',
                value2: charger.localName || '-'
              }
            }, {
              onClick: () => setIsEditingChargerValues(charger),
              value: {
                type: TableCellTypes.Icon,
                src: 'general.edit',
                color: 'gray_900'
              }
            }, {
              custom: <ChargerDetailsHint onClick={() => setDetailsCharger(charger)} title={t('chargers_details')} />
            }, {
              link: `/chargers/${charger.chargerId}`,
              value: {
                type: TableCellTypes.OpenButton
              }
            }
          ])} />
        ) : displayedChargers && displayedChargers.length === 0 && (
          <Table id="Chargers" searchBarSlot={filterButton} height="calc(90vh - 170px)" isSearchEnabled={true} isPaginationEnabled={true} triggerRefresh={triggerTableSearchAgain} columns={[
            { width: '30px' },
            { label: t('chargers_chargerId'), width: rfidStatuses ? '11%' : '13%' },
            { label: t('chargers_userIdUserEmail'), width: '20%' },
            { label: t('chargers_status'), width: rfidStatuses ? '6%' : '8%' },
            ...(rfidStatuses ? [{ label: t('chargers_rfidStatus'), width: '6%' }] : []),
            { label: t('chargers_lastOnline'), width: '9%' },
            { label: t('chargers_registrationDate'), width: '9%' },
            { label: t('chargers_fwVersion'), width: '6%' },
            { label: t('chargers_swVersion'), width: '6%' },
            { label: t('chargers_osVersion'), width: '6%' },
            { label: t('chargers_serialNumberLocalName') },
            { width: '12px' },
            { width: '70px' }
          ]} isRenderEmpty={true} data={[[{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}]]} />
        )}
      </> : <>
        {/* Chargers table for users */}
        {displayedChargers && displayedChargers.length > 0 ? (
          <Table id="Chargers" searchBarSlot={filterButton} height="calc(90vh - 170px)" isSearchEnabled={true} isPaginationEnabled={true} triggerRefresh={triggerTableSearchAgain} columns={[
            { width: '30px' },
            { label: t('chargers_chargerName'), width: '25%' },
            { label: t('chargers_chargerId'), width: '25%' },
            { label: t('chargers_status'), width: rfidStatuses ? '12.5%' : '25%' },
            ...(rfidStatuses ? [{ label: t('chargers_rfidStatus'), width: '12.5%' }] : []),
            { label: t('chargers_lastOnline') },
            { width: '70px' }
          ]} data={displayedChargers.map(charger => [
            {
              link: `/chargers/${charger.chargerId}`,
              value: {
                type: TableCellTypes.Image,
                src: charger.online ? 'chargers.chargerOnline' : 'chargers.chargerOffline'
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.localName || '-',
              value: {
                type: TableCellTypes.Label,
                value: charger.localName || '-'
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.chargerId,
              value: {
                type: TableCellTypes.Label,
                value: charger.chargerId
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.online ? -Infinity : new Date(charger.lastPresence.replace(/\./g, '-')).valueOf(),
              value: {
                type: TableCellTypes.Status,
                label: charger.online ? 'Online' : 'Offline',
                themeColor: charger.online ? 'success' : 'gray_400',
                labelBelow: charger.online ? null : dateToTimeAgoString(new Date(charger.lastPresence.replace(/\./g, '-')))
              }
            },
            ...(rfidStatuses ? [{
              link: `/chargers/${charger.chargerId}`,
              sortBy: rfidStatuses[charger.chargerId] || '',
              value: {
                type: TableCellTypes.Status as TableCellTypes.Status,
                label: rfidStatuses[charger.chargerId] ? rfidStatuses[charger.chargerId] : <LoaderDots />,
                themeColor: rfidStatuses[charger.chargerId] ? (rfidStatuses[charger.chargerId] === 'Inactive' || rfidStatuses[charger.chargerId] === '-') ? 'gray_400' : 'success' : 'gray_400'
              }
            }] : []), {
              link: `/chargers/${charger.chargerId}`,
              sortBy: charger.lastPresence,
              value: {
                type: TableCellTypes.LabelDivided,
                value1: charger.lastPresence != '1900.01.01 01:01' ? charger.lastPresence.split(' ')[0] : '-',
                value2: charger.lastPresence != '1900.01.01 01:01' ? charger.lastPresence.split(' ')[1] : ''
              }
            }, {
              link: `/chargers/${charger.chargerId}`,
              value: {
                type: TableCellTypes.OpenButton
              }
            }
          ])} />
        ) : displayedChargers && displayedChargers.length === 0 && (
          <Table id="Chargers" searchBarSlot={filterButton} height="calc(90vh - 170px)" isSearchEnabled={true} isPaginationEnabled={true} triggerRefresh={triggerTableSearchAgain} columns={[
            { width: '30px' },
            { label: t('chargers_chargerName'), width: '25%' },
            { label: t('chargers_chargerId'), width: '25%' },
            { label: t('chargers_status'), width: rfidStatuses ? '12.5%' : '25%' },
            ...(rfidStatuses ? [{ label: t('chargers_rfidStatus'), width: '12.5%' }] : []),
            { label: t('chargers_lastOnline') },
            { width: '70px' }
          ]} isRenderEmpty={true} data={[[{}, {}, {}, {}, {}, {}, {}]]} />
        )}
      </>}
    </>
  )
}